import { useEffect, useRef, useState } from "react";
import { renderQrCanvas } from "./renderQr";

export interface QrCanvasProps {
  value: string;
  label: string;
  size?: number;
  onRendered?: (value: string) => void;
}

export function QrCanvas({ value, label, size = 420, onRendered }: QrCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onRenderedRef = useRef(onRendered);
  const [error, setError] = useState<string>();
  const [renderAttempt, setRenderAttempt] = useState(0);

  useEffect(() => {
    onRenderedRef.current = onRendered;
  }, [onRendered]);

  useEffect(() => {
    let cancelled = false;
    const visibleCanvas = canvasRef.current;
    if (visibleCanvas === null) {
      return;
    }

    const stagingCanvas = document.createElement("canvas");
    void renderQrCanvas(stagingCanvas, value, size)
      .then(() => {
        if (cancelled) {
          return;
        }
        const context = visibleCanvas.getContext("2d");
        if (context === null) {
          throw new Error("The QR canvas is unavailable.");
        }
        visibleCanvas.width = stagingCanvas.width;
        visibleCanvas.height = stagingCanvas.height;
        context.drawImage(stagingCanvas, 0, 0);
        setError(undefined);
        onRenderedRef.current?.(value);
      })
      .catch(() => {
        if (!cancelled) {
          setError("This QR code could not be rendered.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [renderAttempt, size, value]);

  return (
    <figure className="qr-transfer__qr-figure">
      <div className="qr-transfer__qr-paper">
        <canvas
          ref={canvasRef}
          aria-label={label}
          className="qr-transfer__qr-canvas"
          role="img"
        />
      </div>
      {error === undefined ? null : (
        <figcaption className="qr-transfer__message qr-transfer__message--danger">
          <span>{error} Playback is paused on this frame.</span>
          <button
            className="qr-transfer__button qr-transfer__button--secondary"
            onClick={() => {
              setError(undefined);
              setRenderAttempt((attempt) => attempt + 1);
            }}
            type="button"
          >
            Retry QR
          </button>
        </figcaption>
      )}
    </figure>
  );
}
